import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Input, Option, Select, Spinner } from '@material-tailwind/react';
import { PlusIcon, SearchIcon, Trash2Icon, PencilIcon, ArrowRightIcon } from 'lucide-react';
import { INVENTORY_CATEGORIES_ENUM } from '../../utils/enums';
import { URLS } from '../../router-x/routes';
import { usePermissions } from '../../custom-hooks/use-permissions';
import { useConfirm } from '../../custom-hooks/confirm-dialog';
import { useGetInventoryItems, useDeleteInventoryItem } from '../../custom-hooks/use-inventory';
import { CATEGORY_META } from './utils';
import ItemFormModal from './components/ItemFormModal';
import AddTransactionModal from './components/AddTransactionModal';

const ItemsView = () => {
    const { can } = usePermissions();
    const { confirm, ConfirmationDialog } = useConfirm();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const stockParam = searchParams.get('stock');

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [category, setCategory] = useState('');
    const [status, setStatus] = useState(stockParam === 'low' ? 'low_stock' : '');
    const [formItem, setFormItem] = useState(undefined); // undefined = closed, null = add, object = edit
    const [logItem, setLogItem] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 400);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const { data: items = [], isLoading, isError } = useGetInventoryItems({
        _search: debouncedSearch || undefined,
        category: category || undefined,
        status: status || undefined,
    });
    const deleteMutation = useDeleteInventoryItem();

    const canManage = can('FX31', 'manage_tab');
    const canEditItems = can('FX31', 'edit_items');

    const handleDeactivate = async (item) => {
        const ok = await confirm({
            title: 'Deactivate Item',
            message: `Hide "${item.name}" from active inventory? Its history is kept.`,
            confirmText: 'Deactivate',
            confirmBtnColor: 'red',
        });
        if (ok) await deleteMutation.mutateAsync(item._id);
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-inv-cream-200 bg-white p-4">
                <div className="w-full min-w-[220px] flex-1 sm:w-auto">
                    <Input
                        label="Search items"
                        icon={<SearchIcon size={16} />}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="w-full shrink-0 sm:w-56">
                    <Select
                        label="Category"
                        value={category}
                        onChange={(v) => setCategory(v)}
                        selected={() => (category ? CATEGORY_META[category]?.label || category : 'All Categories')}
                    >
                        <Option value="">All Categories</Option>
                        {INVENTORY_CATEGORIES_ENUM.map((cat) => (
                            <Option key={cat} value={cat}>{CATEGORY_META[cat]?.label || cat}</Option>
                        ))}
                    </Select>
                </div>
                <div className="w-full shrink-0 sm:w-56">
                    <Select
                        label="Status"
                        value={status}
                        onChange={(v) => setStatus(v)}
                        selected={() => ({ '': 'All', low_stock: 'Low Stock', ok: 'OK' }[status] || 'All')}
                    >
                        <Option value="">All</Option>
                        <Option value="low_stock">Low Stock</Option>
                        <Option value="ok">OK</Option>
                    </Select>
                </div>
                {canManage && (
                    <button
                        onClick={() => setFormItem(null)}
                        className="flex w-full shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-inv-brand px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-inv-brand-dark sm:ml-auto sm:w-auto"
                    >
                        <PlusIcon size={16} /> Add Item
                    </button>
                )}
            </div>

            <div className="overflow-hidden rounded-2xl border border-inv-cream-200 bg-white">
                {isLoading ? (
                    <div className="flex justify-center py-10"><Spinner className="h-6 w-6 text-inv-brand" /></div>
                ) : isError ? (
                    <div className="py-10 text-center text-[#8C8175]">Failed to load items.</div>
                ) : items.length === 0 ? (
                    <div className="py-10 text-center text-sm text-[#8C8175]">No items match your filters.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm md:min-w-[640px]">
                            <thead>
                                <tr className="border-b border-inv-cream-200 text-xs font-semibold uppercase tracking-wide text-[#8C8175]">
                                    <th className="px-4 py-3">Item</th>
                                    <th className="hidden px-4 py-3 md:table-cell">Category</th>
                                    <th className="px-4 py-3">Remaining</th>
                                    <th className="hidden px-4 py-3 md:table-cell">Reorder Level</th>
                                    <th className="hidden px-4 py-3 md:table-cell">Daily Max</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => {
                                    const isLow = item.reorderLevel > 0 && item.currentRemaining <= item.reorderLevel;
                                    const meta = CATEGORY_META[item.category] || CATEGORY_META.others;
                                    return (
                                        <tr
                                            key={item._id}
                                            onClick={() => navigate(`${URLS.ADMIN.INVENTORY}/${item._id}`)}
                                            className="cursor-pointer border-b border-inv-cream-100 last:border-0 hover:bg-inv-cream"
                                        >
                                            <td className="px-4 py-3">
                                                <span className="font-semibold text-inv-ink">
                                                    {item.name}
                                                </span>
                                                {/* Category column is hidden below md — keep it visible here as a small tag instead */}
                                                <span className="mt-0.5 flex items-center gap-1.5 text-xs text-[#6B675F] md:hidden">
                                                    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.hex }} />
                                                    {meta.label}
                                                </span>
                                            </td>
                                            <td className="hidden px-4 py-3 md:table-cell">
                                                <span className="inline-flex items-center gap-1.5 text-xs text-[#6B675F]">
                                                    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.hex }} />
                                                    {meta.label}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`font-bold ${isLow ? 'text-red-600' : 'text-inv-ink'}`}>{item.currentRemaining}</span>
                                                <span className="ml-1 text-xs text-[#8C8175]">{item.unitOfMeasure}</span>
                                            </td>
                                            <td className="hidden px-4 py-3 text-[#6B675F] md:table-cell">{item.reorderLevel}</td>
                                            <td className="hidden px-4 py-3 text-[#6B675F] md:table-cell">{item.dailyMaxLimit ?? '—'}</td>
                                            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex justify-end gap-1.5">
                                                    {can('FX31', 'log') && (
                                                        <button
                                                            onClick={() => setLogItem(item)}
                                                            className="hidden h-8 w-8 items-center justify-center rounded-lg text-inv-brand hover:bg-inv-brand-light md:flex"
                                                            title="Log entry"
                                                        >
                                                            <PlusIcon size={15} />
                                                        </button>
                                                    )}
                                                    {canEditItems && (
                                                        <>
                                                            <button
                                                                onClick={() => setFormItem(item)}
                                                                className="hidden h-8 w-8 items-center justify-center rounded-lg text-[#6B675F] hover:bg-inv-cream-100 md:flex"
                                                                title="Edit item"
                                                            >
                                                                <PencilIcon size={14} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeactivate(item)}
                                                                className="flex h-8 w-8 items-center justify-center rounded-lg text-red-500 hover:bg-red-50"
                                                                title="Deactivate item"
                                                            >
                                                                <Trash2Icon size={14} />
                                                            </button>
                                                        </>
                                                    )}
                                                    <Link
                                                        to={`${URLS.ADMIN.INVENTORY}/${item._id}`}
                                                        className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B675F] hover:bg-inv-cream-100"
                                                        title="View daily log"
                                                    >
                                                        <ArrowRightIcon size={14} />
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <ItemFormModal isOpen={formItem !== undefined} onClose={() => setFormItem(undefined)} item={formItem} />
            <AddTransactionModal isOpen={!!logItem} onClose={() => setLogItem(null)} item={logItem} />
            <ConfirmationDialog />
        </div>
    );
};

export default ItemsView;
